import { PieChart } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { AppraisalSection } from '@/components/deals/appraisal/AppraisalSection'
import {
  CONDITIONAL_COSTS,
  SA_COSTS,
  STRATEGY_COSTS,
} from '@/components/deals/appraisal/appraisal-data'
import { cn } from '@/lib/utils'

const BASE_COSTS = [
  {
    label: 'Refurb / Build Cost',
    defaultValue: '',
    prefix: '£',
    source: 'AI Estimation Coming Soon',
    confidence: 'Medium',
  },
  {
    label: 'Contingency (% of refurb/build)',
    defaultValue: '',
    suffix: '%',
    source: 'Default % of Refurb',
  },
  {
    label: 'Professional Fees',
    defaultValue: '',
    prefix: '£',
    source: 'Manual',
    confidence: 'High',
  },
  {
    label: 'Legal Costs',
    defaultValue: '',
    prefix: '£',
    source: 'Manual',
    confidence: 'High',
  },
  {
    label: 'Survey Costs',
    defaultValue: '',
    prefix: '£',
    source: 'Manual',
    confidence: 'High',
  },
]

const CATEGORY_ORDER = ['Planning & Design', 'Legal & Structuring', 'Operational Setup']

const CONFIDENCE_CLASS_NAMES = {
  High: 'bg-primary/10 text-primary',
  Medium: 'bg-chart-4/10 text-chart-4',
  Low: 'bg-muted text-muted-foreground',
}

export function CostAssumptionsPanel({
  currentStrategy,
  exitMode,
  isSAEnabled,
  baseCostValues,
  conditionalCostValues,
  conditionalCostToggles,
  disposalCostValues,
  onUpdateBaseCost,
  onServicedAccommodationChange,
  onToggleConditionalCost,
  onUpdateConditionalCost,
  onUpdateDisposalCost,
}) {
  const costNames = activeCostNames(currentStrategy, isSAEnabled)
  const groupedCosts = groupCostsByCategory(costNames)

  return (
    <AppraisalSection title="Cost Assumptions" icon={PieChart}>
      <div className="space-y-6">
        <section className="space-y-4">
          <h3 className="border-b border-border/50 pb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Base Costs
          </h3>
          <div className="space-y-4">
            {BASE_COSTS.map((cost) => (
              <AssumptionRow
                key={cost.label}
                cost={cost}
                value={baseCostValues?.[cost.label] ?? cost.defaultValue}
                onChange={(value) => onUpdateBaseCost?.(cost.label, value)}
              />
            ))}
          </div>
        </section>

        {exitMode === 'Sell' ? (
          <section className="space-y-4 pt-2">
            <h3 className="border-b border-border/50 pb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Disposal Costs
            </h3>
            <div className="space-y-4">
              <AssumptionRow
                cost={{
                  label: 'Estate Agent Fee',
                  suffix: '%',
                  source: 'Percentage of sale price',
                  confidence: 'High',
                }}
                value={disposalCostValues?.estateAgentFeePercent ?? '1.5'}
                onChange={(value) => onUpdateDisposalCost?.('estateAgentFeePercent', value)}
              />
              <AssumptionRow
                cost={{
                  label: 'Sale Legal Fee',
                  prefix: '£',
                  source: 'Manual',
                  confidence: 'High',
                }}
                value={disposalCostValues?.saleLegalFee ?? ''}
                onChange={(value) => onUpdateDisposalCost?.('saleLegalFee', value)}
              />
            </div>
          </section>
        ) : null}

        <section className="space-y-4 pt-2">
          <div className="flex flex-col gap-3 border-b border-border/50 pb-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-foreground">
                Conditional Costs
              </h3>
              <p className="mt-1 max-w-[18rem] text-[10px] font-medium leading-relaxed text-muted-foreground">
                Auto-configured based on strategy and deal type. Excludes purchase and finance costs (handled in appraisal).
              </p>
            </div>
            <div className="flex w-fit items-center gap-3 rounded-lg border border-border/50 bg-muted/30 px-3 py-2">
              <span className="text-[10px] font-bold text-muted-foreground">
                Enable Serviced Accommodation Setup
              </span>
              <Switch
                size="sm"
                checked={isSAEnabled}
                aria-label="Enable Serviced Accommodation Setup"
                onCheckedChange={(checked) => onServicedAccommodationChange?.(checked === true)}
              />
            </div>
          </div>

          <div className="space-y-5">
            {CATEGORY_ORDER.map((category) => {
              const costs = groupedCosts[category] ?? []
              if (!costs.length) return null

              return (
                <section key={category} className="space-y-3 pt-2">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {category}
                  </h4>
                  <div className="space-y-3">
                    {costs.map((cost) => (
                      <ConditionalCostRow
                        key={cost.name}
                        cost={cost}
                        value={conditionalCostValues?.[cost.name] ?? cost.defaultValue ?? ''}
                        isEnabled={conditionalCostToggles?.[cost.name] ?? true}
                        onToggle={onToggleConditionalCost}
                        onUpdate={onUpdateConditionalCost}
                      />
                    ))}
                  </div>
                </section>
              )
            })}
          </div>
        </section>
      </div>
    </AppraisalSection>
  )
}

function AssumptionRow({ cost, value, onChange }) {
  return (
    <div className="group flex flex-col gap-4 rounded-xl border border-border/60 bg-muted/10 p-4 transition-colors hover:border-border hover:bg-muted/20 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <span className="mb-1.5 block text-sm font-bold text-foreground">
          {cost.label}
        </span>
        <div className="flex flex-wrap gap-2">
          <Chip>{cost.source}</Chip>
          {cost.confidence ? (
            <ConfidenceChip confidence={cost.confidence} />
          ) : null}
        </div>
      </div>
      <MoneyInput
        ariaLabel={cost.label}
        value={value}
        prefix={cost.prefix}
        suffix={cost.suffix}
        onChange={onChange}
      />
    </div>
  )
}

function ConditionalCostRow({ cost, value, isEnabled, onToggle, onUpdate }) {
  return (
    <div
      className={cn(
        'group flex flex-col gap-4 rounded-xl border p-4 transition-colors sm:flex-row sm:items-center sm:justify-between',
        isEnabled
          ? 'border-border/60 bg-muted/10 hover:border-border hover:bg-muted/20'
          : 'border-border/30 bg-transparent opacity-60',
      )}
    >
      <div className="flex items-center gap-4">
        <Switch
          size="sm"
          checked={isEnabled}
          aria-label={`Enable ${cost.name}`}
          onCheckedChange={(checked) => onToggle?.(cost.name, checked === true)}
        />
        <div>
          <span
            className={cn(
              'mb-1.5 block text-sm font-bold',
              isEnabled ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {cost.name}
          </span>
          <div className="flex flex-wrap gap-2">
            <Chip>AI Estimation Coming Soon</Chip>
            <ConfidenceChip confidence={cost.confidence} isEnabled={isEnabled} />
          </div>
        </div>
      </div>
      <MoneyInput
        ariaLabel={`Cost - ${cost.name}`}
        value={value}
        prefix="£"
        disabled={!isEnabled}
        onChange={(nextValue) => onUpdate?.(cost.name, nextValue)}
      />
    </div>
  )
}

function MoneyInput({ ariaLabel, value, prefix, suffix, disabled, onChange }) {
  return (
    <div className="relative shrink-0">
      {prefix ? (
        <span
          aria-hidden="true"
          className={cn(
            'absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold pointer-events-none',
            disabled ? 'text-muted-foreground/50' : 'text-muted-foreground',
          )}
        >
          {prefix}
        </span>
      ) : null}
      <Input
        value={value}
        disabled={disabled}
        inputMode="decimal"
        aria-label={ariaLabel}
        onChange={(event) => onChange?.(event.target.value)}
        className={cn(
          'w-32 border-border/80 bg-background py-2 text-right text-sm font-bold text-foreground transition-colors hover:border-border focus-visible:ring-primary/20',
          prefix && 'pl-7',
          suffix ? 'pr-8' : 'pr-4',
          disabled && 'border-transparent bg-muted/30 text-muted-foreground',
        )}
      />
      {suffix ? (
        <span
          aria-hidden="true"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground pointer-events-none"
        >
          {suffix}
        </span>
      ) : null}
    </div>
  )
}

function Chip({ children }) {
  return (
    <span className="rounded bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </span>
  )
}

function ConfidenceChip({ confidence, isEnabled = true }) {
  return (
    <span
      className={cn(
        'rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
        isEnabled ? CONFIDENCE_CLASS_NAMES[confidence] : 'bg-muted text-muted-foreground',
      )}
    >
      {confidence} Conf
    </span>
  )
}

function activeCostNames(currentStrategy, isSAEnabled) {
  const strategyCosts = STRATEGY_COSTS[currentStrategy] ?? STRATEGY_COSTS['BRRR / Flip']

  return [
    ...new Set([
      ...strategyCosts,
      ...(isSAEnabled ? SA_COSTS : []),
    ]),
  ]
}

function groupCostsByCategory(costNames) {
  return costNames.reduce((groups, name) => {
    const cost = CONDITIONAL_COSTS[name]
    if (!cost) return groups

    const category = cost.category ?? 'Other'

    return {
      ...groups,
      [category]: [
        ...(groups[category] ?? []),
        { ...cost, name },
      ],
    }
  }, {})
}
